import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Layout } from '../../components/layout/Layout';
import { AnalyticsGlassMetricCard } from '../../components/student/AnalyticsGlassMetricCard';
import { useTutorAnalytics } from '../../tutor/hooks/useTutorAnalytics';
import { getWritingChecks } from '../../modules/learning/api/writing';
import type { WritingCheckListItem } from '../../modules/learning/api/writing';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { AnalyticsReportView } from './AnalyticsReportView';
import { exportElementToPdf } from '../../utils/pdfExport';
import { showSuccessToast, showErrorToast } from '../../utils/errorHandler';
import {
  BarChart3,
  Clock,
  Users,
  Activity,
  Mic,
  TrendingUp,
  Calendar,
  MessageSquare,
  Search,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Eye,
  EyeOff,
  AlertTriangle,
  Edit3,
  ExternalLink,
  LineChart,
  Loader2
} from 'lucide-react';
import {
  resolveSpeakingTopicLabel,
  type TutorSessionDoc,
  type SpeakingMetricsDoc,
  type DailyEngagementDoc,
} from '../../tutor/types/tutorAnalytics';
import { ROUTES } from '../../utils/constants';

const clampScore = (value: number | undefined) =>
  Math.max(0, Math.min(100, value ?? 0));

const truncateText = (text: string | undefined, maxLen: number): string => {
  if (!text || typeof text !== 'string') return '—';
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen) + '…';
};

const getWritingOverallScore = (item: WritingCheckListItem): number | null => {
  const score =
    item.derived_metadata?.overall_score ??
    item.result?.rubric?.overallScore;
  if (score == null) return null;
  return clampScore(score);
};

export const StudentAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMode, setSelectedMode] = useState<string>('all');
  const [selectedTutorType, setSelectedTutorType] = useState<string>('all');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const [writingItems, setWritingItems] = useState<WritingCheckListItem[]>([]);
  const [writingTotal, setWritingTotal] = useState<number | undefined>();
  const [writingLoading, setWritingLoading] = useState(true);
  const [writingError, setWritingError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const reportContainerRef = useRef<HTMLDivElement>(null);

  const { data, loading, error, refetch, days, setDays } = useTutorAnalytics({
    initialDays: 30,
  });

  const fetchWritingChecks = useCallback(async () => {
    setWritingLoading(true);
    setWritingError(null);
    try {
      const res = await getWritingChecks({ limit: 20, skip: 0 });
      setWritingItems(res.items);
      setWritingTotal(res.total);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not load writing checks.';
      setWritingError(message);
      setWritingItems([]);
    } finally {
      setWritingLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWritingChecks();
  }, [fetchWritingChecks]);

  useEffect(() => {
    if (!isExporting || !reportContainerRef.current) return;

    // INCREASED TIMEOUT TO 1500ms
    // This allows responsive charts to calculate their width AND finish their drawing animations 
    // before the PDF capture takes a snapshot.
    const timer = setTimeout(() => {
      if (!reportContainerRef.current) {
        setIsExporting(false);
        return;
      }
      exportElementToPdf(reportContainerRef.current, {
        filename: 'student-analytics-report',
        delayBeforeCapture: 0,
      })
        .then(() => showSuccessToast('Report downloaded'))
        .catch((err) => showErrorToast(err instanceof Error ? err : String(err)))
        .finally(() => setIsExporting(false));
    }, 1500);

    return () => clearTimeout(timer);
  }, [isExporting]);

  // Show a warning if there are persistent API issues
  const showApiWarning = error && (error.includes('Server error') || error.includes('Cannot connect') || error.includes('not found'));

  const dayOptions = [7, 14, 30, 90];

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  // Filter and search data
  const filteredData = useMemo(() => {
    if (!data) return null;

    let filteredSessions = data.session_history;
    let filteredSpeaking = data.speaking_sessions;

    // Filter by mode
    if (selectedMode !== 'all') {
      filteredSessions = filteredSessions.filter(s => s.mode === selectedMode);
      // For speaking sessions, we need to match by session_id
      const sessionIds = new Set(filteredSessions.map(s => s.session_id));
      filteredSpeaking = filteredSpeaking.filter(s => sessionIds.has(s.session_id));
    }

    // Filter by tutor type
    if (selectedTutorType !== 'all') {
      filteredSessions = filteredSessions.filter(s => s.tutor_type === selectedTutorType);
      const sessionIds = new Set(filteredSessions.map(s => s.session_id));
      filteredSpeaking = filteredSpeaking.filter(s => sessionIds.has(s.session_id));
    }

    // Search in session data (include resolved topic from speaking doc when present)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const speakingBySessionId = new Map(
        data.speaking_sessions.map((s) => [s.session_id, s]),
      );
      filteredSessions = filteredSessions.filter((s) => {
        const sp = speakingBySessionId.get(s.session_id);
        const topicHaystack = sp
          ? resolveSpeakingTopicLabel(sp, data.session_history).toLowerCase()
          : (s.topic_label || '').toLowerCase();
        return (
          s.session_id.toLowerCase().includes(term) ||
          s.mode.toLowerCase().includes(term) ||
          s.tutor_type.toLowerCase().includes(term) ||
          (s.topic_label && s.topic_label.toLowerCase().includes(term)) ||
          (s.lesson_no && s.lesson_no.toString().includes(term)) ||
          topicHaystack.includes(term)
        );
      });

      const sessionIds = new Set(filteredSessions.map((s) => s.session_id));
      filteredSpeaking = data.speaking_sessions.filter((s) => sessionIds.has(s.session_id));
    }

    return {
      ...data,
      session_history: filteredSessions,
      speaking_sessions: filteredSpeaking,
    };
  }, [data, selectedMode, selectedTutorType, searchTerm]);

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const mapTutorType = (t?: string) => {
    if (!t) return 'Unknown';
    if (t === 'FREE') return 'AI Lesson Tutor';
    if (t === 'TOPIC') return 'Communication Tutor';
    return t;
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  if (loading) {
    return (
      <Layout>
        <div
          className={clsx(
            'rounded-[2rem] border px-4 py-6 md:px-8 md:py-10 space-y-6 shadow-lg backdrop-blur-xl',
            'border-purple-200/70 bg-white/80 shadow-purple-500/[0.06]',
            'dark:border-purple-500/25 dark:bg-neutral-950 dark:shadow-[0_32px_80px_-28px_rgba(147,51,234,0.14)] dark:backdrop-blur-none'
          )}
        >
          <div className="animate-pulse space-y-4">
            <div className="h-10 w-2/3 max-w-md rounded-2xl bg-slate-200/80 dark:bg-white/10" />
            <div className="h-24 rounded-[1.75rem] bg-slate-100/90 ring-1 ring-slate-200/80 backdrop-blur-xl dark:bg-white/5 dark:ring-white/10" />
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className={clsx(
                    'relative h-32 overflow-hidden rounded-3xl backdrop-blur-xl ring-1',
                    'bg-slate-100/70 ring-slate-200/70',
                    'dark:bg-white/[0.04] dark:ring-white/[0.07]',
                    i === 0 && 'lg:col-span-2'
                  )}
                >
                  <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500/50 via-purple-500/50 to-pink-500/50" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !filteredData) {
    return (
      <Layout>
        <div
          className={clsx(
            'rounded-[2rem] border px-4 py-8 md:px-8 md:py-12 shadow-lg backdrop-blur-xl',
            'border-purple-200/70 bg-white/80 shadow-purple-500/[0.06]',
            'dark:border-purple-500/25 dark:bg-neutral-950 dark:shadow-[0_32px_80px_-28px_rgba(147,51,234,0.14)] dark:backdrop-blur-none'
          )}
        >
          <div
            className={clsx(
              'rounded-[1.5rem] border px-6 py-12 text-center backdrop-blur-xl',
              'border-slate-200/80 bg-white/90 text-slate-800',
              'dark:border-white/10 dark:bg-slate-950/70 dark:text-white'
            )}
          >
            <BarChart3 className="mx-auto mb-4 h-16 w-16 text-purple-500 dark:text-purple-400/85" />
            <h3 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">
              {error || 'No analytics data available'}
            </h3>
            <p className="text-slate-600 dark:text-white/55">
              Try refreshing the page or contact support if the problem persists.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const latestWritingScoreDisplay =
    writingLoading || writingItems.length === 0
      ? '—'
      : (() => {
          const sorted = [...writingItems].sort(
            (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime()
          );
          const score = getWritingOverallScore(sorted[0]);
          return score != null ? `${score}%` : '—';
        })();

  const avgWritingScoreDisplay =
    writingLoading || writingItems.length === 0
      ? '—'
      : (() => {
          const scores = writingItems.map(getWritingOverallScore).filter((s): s is number => s != null);
          if (scores.length === 0) return '—';
          const avg = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
          return `${avg}%`;
        })();

  const totalSuggestionsDisplay = writingLoading
    ? '—'
    : writingItems.reduce((sum, item) => {
        const n =
          item.derived_metadata?.suggestion_count ??
          (Array.isArray((item as any).result?.suggestions) ? (item as any).result.suggestions.length : 0);
        return sum + (typeof n === 'number' ? n : 0);
      }, 0);

  return (
    <Layout>
      <div
        className={clsx(
          'rounded-[2rem] border px-4 py-6 md:px-8 md:py-10 space-y-8 shadow-lg backdrop-blur-xl',
          'border-purple-200/80 bg-white/80 shadow-purple-500/[0.07]',
          'dark:border-purple-500/30 dark:bg-neutral-950 dark:shadow-[0_32px_80px_-28px_rgba(147,51,234,0.18)] dark:backdrop-blur-none'
        )}
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="relative flex h-3 w-3 shrink-0" aria-hidden title="Live insights">
                <span className="absolute inset-0 animate-ping rounded-full bg-emerald-400/35" />
                <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500 shadow-[0_0_14px_4px_rgba(52,211,153,0.65)] dark:bg-emerald-400" />
              </span>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
                Analytics Dashboard
              </h1>
            </div>
            <p className="mt-2 max-w-xl text-sm text-slate-600 dark:text-white/55">
              Activity from Tamil Tutor speaking practice only — Total practice time is highlighted first.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsExporting(true)}
              disabled={isExporting}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-purple-500/25 transition hover:brightness-110 disabled:opacity-60"
            >
              {isExporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {isExporting ? 'Preparing…' : 'Export report'}
            </button>
            <button
              type="button"
              onClick={() => navigate(ROUTES.STUDENT_ANALYTICS_CHARTS)}
              className="inline-flex items-center gap-2 rounded-full border border-purple-300 bg-purple-50/90 px-4 py-2.5 text-sm font-semibold text-purple-900 shadow-sm transition hover:border-fuchsia-400/50 hover:bg-purple-100 dark:border-purple-400/45 dark:bg-purple-500/10 dark:text-purple-100 dark:shadow-none dark:hover:bg-purple-500/20"
            >
              <LineChart className="h-4 w-4" />
              More Charts
            </button>
          </div>
        </div>

        {showApiWarning && (
          <div className="rounded-2xl border border-amber-200/90 bg-amber-50/95 px-4 py-3 backdrop-blur-sm dark:border-yellow-500/35 dark:bg-yellow-500/[0.08]">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2 text-amber-950 dark:text-yellow-100">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-yellow-400" />
                <span className="text-sm">
                  Analytics service temporarily unavailable. Some features may not work properly.
                </span>
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                className="inline-flex shrink-0 items-center justify-center gap-1 rounded-full border border-amber-400/80 px-3 py-1.5 text-xs font-semibold text-amber-950 hover:bg-amber-100/80 dark:border-yellow-400/50 dark:text-yellow-100 dark:hover:bg-yellow-500/15"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Bubbly glass toolbar */}
        <div
          className={clsx(
            'rounded-[1.75rem] border p-4 backdrop-blur-xl md:p-5',
            'border-slate-200/90 bg-white/75 shadow-sm ring-1 ring-slate-200/40',
            'dark:border-white/10 dark:bg-slate-950/70 dark:shadow-none dark:ring-0'
          )}
        >
          <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:gap-x-4 lg:gap-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-white/45">
                Period
              </span>
              {dayOptions.map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={clsx(
                    'rounded-full px-4 py-2 text-xs font-bold transition',
                    days === d
                      ? 'scale-[1.02] bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 text-white shadow-lg shadow-purple-500/30'
                      : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200/90 hover:bg-slate-200/90 dark:bg-white/5 dark:text-white/70 dark:ring-white/10 dark:hover:bg-white/10'
                  )}
                >
                  {d}d
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="analytics-mode"
                className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-white/45"
              >
                Mode
              </label>
              <select
                id="analytics-mode"
                value={selectedMode}
                onChange={(e) => setSelectedMode(e.target.value)}
                className="rounded-full border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm font-medium text-slate-900 ring-0 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 dark:border-white/15 dark:bg-white/5 dark:text-white dark:focus:border-purple-400/50 dark:focus:ring-purple-500/40"
              >
                <option value="all">All</option>
                <option value="VOICE">Voice</option>
                <option value="TEXT">Text</option>
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <label
                htmlFor="analytics-tutor"
                className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-white/45"
              >
                Tutor
              </label>
              <select
                id="analytics-tutor"
                value={selectedTutorType}
                onChange={(e) => setSelectedTutorType(e.target.value)}
                className="rounded-full border border-slate-200 bg-white py-2 pl-3 pr-8 text-sm font-medium text-slate-900 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500/30 dark:border-white/15 dark:bg-white/5 dark:text-white dark:focus:border-purple-400/50 dark:focus:ring-purple-500/40"
              >
                <option value="all">All</option>
                <option value="FREE">Free</option>
                <option value="TOPIC">Topic</option>
                <option value="LESSON">Lesson</option>
              </select>
            </div>

            <div className="relative min-w-[160px] flex-1 lg:max-w-xs">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400 dark:text-white/35" />
              <input
                type="text"
                placeholder="Search sessions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-full border border-slate-200 bg-white/95 py-2 pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-500/25 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-white/35 dark:focus:border-purple-400/45 dark:focus:ring-purple-500/30"
              />
            </div>

            <button
              type="button"
              onClick={() => refetch()}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-purple-300 bg-gradient-to-r from-indigo-50 via-purple-50 to-pink-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-purple-900 shadow-sm transition hover:border-purple-400 hover:from-indigo-100/90 hover:via-purple-100/90 hover:to-pink-100/90 disabled:opacity-50 dark:border-fuchsia-400/40 dark:from-indigo-600/15 dark:via-purple-600/15 dark:to-pink-600/15 dark:text-purple-100 dark:shadow-none dark:hover:border-purple-300/45 dark:hover:from-indigo-600/25 dark:hover:via-purple-600/25 dark:hover:to-pink-600/25"
            >
              <RefreshCw className={clsx('h-4 w-4', loading && 'animate-spin')} />
              Refresh
            </button>
          </div>
        </div>

        {/* 8 glass metric cards — Total Minutes is primary (2× width on lg) */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <AnalyticsGlassMetricCard
            hero
            icon={Clock}
            glow="indigo"
            label="Total minutes"
            value={
              filteredData.total_tutor_minutes > 0
                ? `${Math.floor(filteredData.total_tutor_minutes / 60)}h ${filteredData.total_tutor_minutes % 60}m`
                : '—'
            }
          />
          <AnalyticsGlassMetricCard
            icon={Users}
            glow="cyan"
            label="Sessions"
            value={filteredData.sessions_count || '—'}
          />
          <AnalyticsGlassMetricCard
            icon={Mic}
            glow="purple"
            label="Speaking level"
            value={filteredData.latest_speaking_level || '—'}
          />
          <AnalyticsGlassMetricCard
            icon={TrendingUp}
            glow="pink"
            label="Avg score"
            value={filteredData.latest_overall_score !== null ? `${filteredData.latest_overall_score}%` : '—'}
          />
          <AnalyticsGlassMetricCard
            icon={Edit3}
            glow="fuchsia"
            label="Writing checks"
            value={writingLoading ? '—' : String((writingTotal ?? writingItems.length) ?? '0')}
          />
          <AnalyticsGlassMetricCard
            icon={TrendingUp}
            glow="violet"
            label="Latest writing score"
            value={latestWritingScoreDisplay}
          />
          <AnalyticsGlassMetricCard
            icon={BarChart3}
            glow="purple"
            label="Avg writing score"
            value={avgWritingScoreDisplay}
          />
          <AnalyticsGlassMetricCard
            icon={MessageSquare}
            glow="cyan"
            label="Total suggestions"
            value={totalSuggestionsDisplay}
          />
        </div>

        {/* Expandable Sections */}
        <div className="space-y-4">
          {/* Session History */}
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => toggleSection('sessions')}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Session History ({filteredData.session_history.length} sessions)
                </div>
                {expandedSections.has('sessions') ?
                  <ChevronUp className="w-5 h-5" /> :
                  <ChevronDown className="w-5 h-5" />
                }
              </CardTitle>
            </CardHeader>
            {expandedSections.has('sessions') && (
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredData.session_history.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No sessions found</p>
                  ) : (
                    filteredData.session_history.map((session, idx) => (
                      <div key={session?.session_id || Math.random()} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 dark:text-white">
                                Session {idx + 1}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${session?.mode === 'VOICE'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                  : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                }`}>
                                {session?.mode || 'Unknown'}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs ${session?.tutor_type === 'FREE'
                                  ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                  : 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-950/60 dark:text-fuchsia-200'
                                }`}>
                                {mapTutorType(session?.tutor_type)}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {session?.started_at ? formatDate(session.started_at) : 'Unknown date'} • Duration: {session?.duration_seconds ? formatDuration(session.duration_seconds) : 'Unknown'}
                            </div>
                          </div>
                          <div className="text-right text-sm">
                            <div className="text-gray-600 dark:text-gray-400">
                              {session?.turns_user || 0} user • {session?.turns_model || 0} assistant
                            </div>
                            {session?.lesson_no && (
                              <div className="text-gray-600 dark:text-gray-400">
                                Lesson {session.lesson_no}
                              </div>
                            )}
                            {session?.topic_label && (
                              <div className="text-gray-600 dark:text-gray-400">
                                {session.topic_label}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Speaking Sessions */}
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => toggleSection('speaking')}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Mic className="w-5 h-5" />
                  Speaking Sessions ({filteredData.speaking_sessions.length} evaluations)
                </div>
                {expandedSections.has('speaking') ?
                  <ChevronUp className="w-5 h-5" /> :
                  <ChevronDown className="w-5 h-5" />
                }
              </CardTitle>
            </CardHeader>
            {expandedSections.has('speaking') && (
              <CardContent>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {filteredData.speaking_sessions.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No speaking evaluations found</p>
                  ) : (
                    filteredData.speaking_sessions.map((session, idx) => (
                      <div key={session?.session_id || Math.random()} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-gray-900 dark:text-white">
                                Evaluation {idx + 1}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200`}>
                                {session?.speaking_level || 'Unknown'}
                              </span>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {session?.created_at ? formatDate(session.created_at) : 'Unknown date'} • Duration: {session?.duration_seconds ? formatDuration(session.duration_seconds) : 'Unknown'}
                            </div>
                            <div className="text-sm mt-1.5 text-gray-800 dark:text-gray-200">
                              <span className="text-gray-500 dark:text-gray-400">Topic: </span>
                              <span className="font-medium">
                                {session
                                  ? resolveSpeakingTopicLabel(session, filteredData.session_history) || '—'
                                  : '—'}
                              </span>
                              {session?.tutor_type === 'TOPIC' && (
                                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-pink-500/15 text-pink-800 ring-1 ring-pink-500/25 dark:bg-pink-950/45 dark:text-pink-200 dark:ring-pink-400/30">
                                  Communication
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className={`text-2xl font-bold ${session?.scores?.overall ? getScoreColor(session.scores.overall) : 'text-gray-500'}`}>
                              {session?.scores?.overall || 0}%
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">Overall Score</div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.pronunciation)}`}>
                              {session.scores.pronunciation}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Pronunciation</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.fluency)}`}>
                              {session.scores.fluency}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Fluency</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.grammar)}`}>
                              {session.scores.grammar}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Grammar</div>
                          </div>
                          <div className="text-center">
                            <div className={`text-lg font-semibold ${getScoreColor(session.scores.vocabulary)}`}>
                              {session.scores.vocabulary}%
                            </div>
                            <div className="text-xs text-gray-600 dark:text-gray-400">Vocabulary</div>
                          </div>
                        </div>

                        {session.feedback_ta && (
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Feedback</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-2 rounded">
                              {session.feedback_ta}
                            </p>
                          </div>
                        )}

                        {session.recommendations && session.recommendations.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Recommendations</h4>
                            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                              {session.recommendations.map((rec, index) => (
                                <li key={index} className="flex items-start gap-2">
                                  <span className="text-blue-500 mt-1">•</span>
                                  <span>{rec}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Daily Engagement */}
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => toggleSection('engagement')}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  Daily Engagement ({filteredData.daily_engagement.length} days)
                </div>
                {expandedSections.has('engagement') ?
                  <ChevronUp className="w-5 h-5" /> :
                  <ChevronDown className="w-5 h-5" />
                }
              </CardTitle>
            </CardHeader>
            {expandedSections.has('engagement') && (
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {filteredData.daily_engagement.length === 0 ? (
                    <div className="text-center py-4 space-y-2">
                      <p className="text-gray-500">No speaking practice recorded for this period.</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Use the Tamil Tutor (speaking) to see activity here. Writing and other activities are not shown in this dashboard.
                      </p>
                    </div>
                  ) : (
                    <>
                      {(() => {
                        const todayStr = new Date().toISOString().slice(0, 10);
                        const hasToday = filteredData.daily_engagement.some((d) => d?.date === todayStr);
                        if (!hasToday) {
                          return (
                            <p className="text-sm text-gray-500 dark:text-gray-400 pb-2 border-b border-gray-200 dark:border-gray-700">
                              No speaking practice recorded for today. Use the Tamil Tutor (speaking) to see activity here.
                            </p>
                          );
                        }
                        return null;
                      })()}
                      {filteredData.daily_engagement.map((day) => (
                        <div key={day?.date || Math.random()} className="flex justify-between items-center p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {day?.date ? new Date(day.date).toLocaleDateString(undefined, {
                                weekday: 'long',
                                month: 'short',
                                day: 'numeric'
                              }) : 'Unknown date'}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              {day?.sessions || 0} sessions • Last active: {day?.last_active_at ? formatDate(day.last_active_at) : 'Unknown'}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-gray-900 dark:text-white">
                              {Math.floor((day?.tutor_minutes || 0) / 60)}h {(day?.tutor_minutes || 0) % 60}m
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              total time
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </CardContent>
            )}
          </Card>

          {/* Writing Check History */}
          <Card>
            <CardHeader
              className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => toggleSection('writing')}
            >
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-5 h-5" />
                  Writing Check History ({writingTotal ?? writingItems.length} checks)
                </div>
                {expandedSections.has('writing') ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </CardTitle>
            </CardHeader>
            {expandedSections.has('writing') && (
              <CardContent>
                <div className="mb-3 flex justify-end">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate(ROUTES.STUDENT_WRITING_HISTORY)}
                    className="inline-flex items-center gap-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View full writing history
                  </Button>
                </div>
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {writingLoading && (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                      <RefreshCw className="w-10 h-10 animate-spin mb-3" />
                      <p className="text-sm font-medium">Loading writing checks…</p>
                    </div>
                  )}
                  {!writingLoading && writingError && (
                    <div className="text-center py-8">
                      <AlertTriangle className="w-12 h-12 text-amber-500 dark:text-amber-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                        {writingError}
                      </p>
                      <Button onClick={fetchWritingChecks} size="sm" variant="outline">
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Retry
                      </Button>
                    </div>
                  )}
                  {!writingLoading && !writingError && writingItems.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 text-center py-8">
                      No writing checks found.
                    </p>
                  )}
                  {!writingLoading && !writingError && writingItems.length > 0 &&
                    writingItems.map((item, idx) => (
                      <div
                        key={item.check_id ?? idx}
                        className="border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <span>
                            {item.created_at
                              ? formatDate(item.created_at)
                              : '—'}
                          </span>
                          {item.student_info?.display_name && (
                            <span>{item.student_info.display_name}</span>
                          )}
                          {item.mode && (
                            <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 text-xs">
                              {item.mode}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-800 dark:text-gray-200 tamil-text mb-3">
                          {truncateText(item.original_text, 120)}
                        </p>
                        <div className="flex flex-wrap gap-3 text-xs items-baseline mb-2">
                          <span>
                            Suggestions:{' '}
                            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                              {item.derived_metadata?.suggestion_count ??
                                (Array.isArray((item as any).result?.suggestions)
                                  ? (item as any).result.suggestions.length
                                  : 0)}
                            </span>
                          </span>
                          <span>
                            Overall:{' '}
                            <span className="text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                              {Math.round(
                                clampScore(
                                  item.result?.rubric?.overallScore ??
                                  item.derived_metadata?.overall_score
                                )
                              )}
                              /100
                            </span>
                          </span>
                          <span>
                            G:{' '}
                            <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                              {Math.round(
                                clampScore(
                                  item.result?.rubric?.grammar ??
                                  item.derived_metadata?.grammar_score
                                )
                              )}
                            </span>
                          </span>
                          <span>
                            C:{' '}
                            <span className="text-sm font-semibold text-sky-600 dark:text-sky-400">
                              {Math.round(
                                clampScore(
                                  item.result?.rubric?.clarity ??
                                  item.derived_metadata?.clarity_score
                                )
                              )}
                            </span>
                          </span>
                          <span>
                            V:{' '}
                            <span className="text-sm font-semibold text-violet-600 dark:text-violet-400">
                              {Math.round(
                                clampScore(
                                  item.result?.rubric?.vocabulary ??
                                  item.derived_metadata?.vocabulary_score
                                )
                              )}
                            </span>
                          </span>
                        </div>
                        {(item.result?.rubric?.feedback ??
                          (item as any).result?.rubric?.teacherNote) && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 border-l-2 border-indigo-400 pl-2 mt-2">
                              {item.result?.rubric?.feedback ??
                                (item as any).result?.rubric?.teacherNote}
                            </p>
                          )}
                        {(item.assignment_id ||
                          item.submission_id ||
                          item.task_id ||
                          item.content_id ||
                          item.lesson_id) && (
                            <div className="text-xs text-gray-400 dark:text-gray-500 mt-2 flex flex-wrap gap-x-3 gap-y-1">
                              {item.assignment_id && (
                                <span>Assignment: {item.assignment_id}</span>
                              )}
                              {item.submission_id && (
                                <span>Submission: {item.submission_id}</span>
                              )}
                              {item.task_id && <span>Task: {item.task_id}</span>}
                              {item.content_id && (
                                <span>Content: {item.content_id}</span>
                              )}
                              {item.lesson_id && (
                                <span>Lesson: {item.lesson_id}</span>
                              )}
                            </div>
                          )}
                      </div>
                    ))}
                </div>
              </CardContent>
            )}
          </Card>
        </div>

        {isExporting && (
          <div
            style={{
              position: 'absolute',
              top: '-20000px',
              left: 0,
              zIndex: -1000,
            }}
            aria-hidden
          >
            <div
              ref={reportContainerRef}
              style={{
                width: '794px', // STRICT A4 WIDTH
                backgroundColor: '#ffffff',
                padding: 0, // CRITICAL: Must be 0 so the PDF slicer doesn't drift
                margin: 0,  // CRITICAL: Must be 0
                color: '#000000'
              }}
            >
              <AnalyticsReportView
                tutorData={data ?? null}
                writingItems={writingItems}
                days={days}
              />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};